import type { DelayedAction } from '../api/ReplicantAPI';
import type { ErrorReporter } from '../common/ErrorReporter';
import DB, { Entry, ScheduledAction } from '../db/DB';
import type { AttributeMap } from '../db/impl/DynamoDB/types';
import { ReplicantError } from '../Errors';
import logger from '../logger';
import type { Replicant, ReplicantConfig } from '../ReplicantConfig';
import { resolveEventHandlerAndWriteMessage } from '../ReplicantEventHandlerMessages';
import type { ScheduledActions } from '../ReplicantScheduledActions';
import type { BatchResult, QueuedScheduledAction, ScheduledActionsQueue } from '../scheduledActionsQueue';
import type SB from '../SchemaBuilder';
import { deduplicate, groupBy, partition } from '../utils/ArrayUtils';
import { mapObject } from '../utils/ObjUtils';
import { Immutable } from '../utils/TypeUtils';
import type { NarrowcastAPIClient } from './NarrowcastAPIClient';
import * as perf from './performanceTracking';
import type {
    CreateEventHandlerAPIFn,
    ExecutionContext,
    GetReadOnlyEntryFn,
    PushDocsToIndexFn,
    ResolveDelayedActionsFn,
} from './ServerReplicant';
import {
    createSystemScheduledActions,
    GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID,
    SYSTEM_SCHEDULED_ACTION_NAMES,
    SystemScheduledActions,
} from './ServerReplicantSystemScheduledActions';
import { withIndexDocGeneration } from './utils/IndexUtils';

export type ExecuteScheduledActionsFn = (actions: QueuedScheduledAction[]) => Promise<BatchResult>;

/**
 * Handles the execution of scheduled actions. Actions are scheduled in `resolveActionScheduleUpdates`.
 *
 * These methods are called by
 * 1. the scheduled Lambda that reads scheduled actions from DB once per minute and enqueues into SQS, and
 * 2. the queue consumer Lambda that consumes the SQS queue.
 */
export default class ServerReplicantScheduledActions<T extends Replicant> {
    private scheduledActions: T['scheduledActions'];
    private systemScheduledActions: SystemScheduledActions;

    constructor(
        private opts: {
            config: ReplicantConfig<T>;
            createEventHandlerApi: CreateEventHandlerAPIFn<T>;
            dateNow: () => number;
            db: DB<T['state'], T['messages']>;
            errorReporter?: ErrorReporter;
            getReadOnlyEntry: GetReadOnlyEntryFn<T>;
            narrowcastAPIClient: NarrowcastAPIClient;
            pushDocsToIndex: PushDocsToIndexFn;
            queue: ScheduledActionsQueue;
            resolveDelayedActions: ResolveDelayedActionsFn;
        },
    ) {
        this.scheduledActions = this.opts.config.scheduledActions ?? ({} as T['scheduledActions']);
        this.systemScheduledActions = createSystemScheduledActions({ config: this.opts.config, db: this.opts.db });
    }

    async enqueueScheduledActionsForTimeslot(timeslotId: number, shards: number[], batchSize = 200) {
        if (!this.opts.queue) {
            return;
        }

        // Read the scheduled event in batches, in case there are a ton of those scheduled.

        let continuationTokens: { [timeslotId: string]: AttributeMap } | undefined;
        do {
            const scheduledActions = await this.opts.db.scheduledActions.fetchAllForTimeslot(
                timeslotId,
                batchSize,
                shards,
                continuationTokens,
            );
            continuationTokens = scheduledActions.continuationTokens;

            const actionIds = Object.keys(scheduledActions.actions);

            logger.info(
                `Running scheduled actions for timeslot ${timeslotId}, shards ${shards.join(', ')} (` +
                    new Date(timeslotId * 1000 * 60).toISOString() +
                    `): ${actionIds.length} actions`,
            );

            // Push actions to queue and delete successfully enqueued actions from DB:
            const queueResult = await this.opts.queue.enqueueScheduledActions(
                actionIds.map((id) => scheduledActionToQueuedAction(id, timeslotId, scheduledActions.actions[id]!)),
            );

            await this.opts.db.scheduledActions.deleteBatch(
                queueResult.successful.map((id) => ({ timeslotId, actionId: queuedActionIdToScheduledActionId(id) })),
            );
        } while (continuationTokens);
    }

    executeScheduledActions: ExecuteScheduledActionsFn = async (actions) => {
        logger.info(`Executing a batch of ${actions.length} scheduled actions`);

        const validActions = actions.filter((action) => {
            const scheduledAction = this.getScheduledAction(action);

            if (!scheduledAction) {
                // Ignore actions that we do not recognize.
                logger.warn('Unknown scheduled action: ' + action.actionName);
                return false;
            }

            const validationError = scheduledAction.schema.validate(action.actionArgs);
            if (validationError) {
                // Ignore scheduled actions with arguments that we do not recognize.
                // They may have been scheduled on a different version of the game.
                logger.warn('Error validating args for action `' + action.actionName + '`: ' + validationError);
                return false;
            }

            return true;
        });

        const [globalSystemActions, userActions] = partition(validActions, (action) =>
            isGlobalSystemScheduledAction(action!),
        );

        const uniqueUserIds = deduplicate(userActions.map((action) => action.userId));

        const entriesArray = await Promise.all(
            uniqueUserIds.map(async (userId) => {
                try {
                    const entryRes = await this.opts.getReadOnlyEntry(userId, { reduceMessages: true });
                    return entryRes ? entryRes.entry : null;
                } catch (error: any) {
                    const err = new ReplicantError('Scheduled actions error: ' + error, 'server_error', 'db_error');
                    err.stack = error.stack;
                    await this.opts.errorReporter?.captureException(err);

                    return null;
                }
            }),
        );

        const entries: { [userId: string]: Entry<any> } = Object.fromEntries(
            entriesArray.filter((entry): entry is Entry<any> => entry !== null).map((entry) => [entry.state.id, entry]),
        );
        const context: ExecutionContext = { entryPromises: mapObject(entries, (_, entry) => Promise.resolve(entry)) };

        // Resolve all the analytics and message post actions at once after executing each scheduled action to improve batching:
        const allGloballyBatchedActions: DelayedAction[] = [];

        const resolveDelayedActions = async (
            delayedActions: DelayedAction[],
            performanceContext?: perf.PerformanceContext,
        ): Promise<void> => {
            const [globallyBatchedActions, otherActions] = partition(
                delayedActions,
                (action) => action?.type === 'postMessage' || action?.type === 'sendAnalyticsEvents',
            );

            allGloballyBatchedActions.push(...globallyBatchedActions);

            await this.opts.resolveDelayedActions(otherActions, {
                ...context,
                performanceTracking: { context: performanceContext },
            });
        };

        const successfulActionIds: string[] = [];
        const newIndexDocs: { [docId: string]: Record<string, unknown> } = {};

        // Attempt to execute scheduled actions, reporting and swallowing any execution errors:
        const globalSystemActionPromises = globalSystemActions.map(async (action) => {
            try {
                await this.executeGlobalSystemScheduledAction(action);
                successfulActionIds.push(action.id);
            } catch (e: any) {
                await this.opts.errorReporter?.captureException(e, undefined, action);
            }
        });

        const actionsPerUser = Object.entries(groupBy(userActions, (action) => action.userId));

        const userActionPromises = actionsPerUser.map(async ([userId, actions]) => {
            const entry = entries[userId];

            if (!entry) {
                const userNotFoundError = new ReplicantError(
                    'Scheduled actions error: no user found',
                    'server_error',
                    'scheduled_actions_error',
                    'error',
                    { userId, actions },
                );

                await this.opts.errorReporter?.captureException(userNotFoundError, { user: { id: userId } });

                return;
            }

            for (let i = 0; i < actions.length; i++) {
                const action = actions[i]!;

                try {
                    const newIndexDoc = await withIndexDocGeneration(
                        userId,
                        entry,
                        this.opts.config,
                        this.opts.dateNow,
                        false,
                        () => this.executeScheduledAction({ action, counter: i, entry, resolveDelayedActions }),
                    );

                    if (newIndexDoc) {
                        newIndexDocs[action.userId] = newIndexDoc;
                    }

                    successfulActionIds.push(action.id);
                } catch (error: any) {
                    await this.opts.errorReporter?.captureException(error, { user: { id: userId } }, action);
                }
            }
        });

        await Promise.all([...globalSystemActionPromises, ...userActionPromises]);

        await Promise.all([
            this.opts.resolveDelayedActions(allGloballyBatchedActions, context).catch(
                (error) =>
                    this.opts.errorReporter?.captureException(error, undefined, {
                        delayedActions: allGloballyBatchedActions,
                    }),
            ),

            this.opts.pushDocsToIndex(newIndexDocs),
        ]);

        const failedActionIds = validActions
            .map((action) => action.id)
            .filter((id) => !successfulActionIds.includes(id));

        return { successful: successfulActionIds, failed: failedActionIds };
    };

    private async executeScheduledAction(args: {
        action: QueuedScheduledAction;
        counter: number;
        entry: Entry<T['state']>;
        resolveDelayedActions: (
            delayedActions: DelayedAction[],
            performanceContext?: perf.PerformanceContext,
        ) => Promise<void>;
    }): Promise<void> {
        const { action, entry, resolveDelayedActions } = args;
        const { actionName, actionArgs, userId } = action;

        const scheduledAction = this.getScheduledAction(action);

        if (!scheduledAction) {
            throw Error(`Scheduled action ${action.actionName} not found`); // Should not happen thanks to validation in executeScheduledActions
        }

        // Prep the API objects passed to actions.
        const delayedActions: DelayedAction[] = [];
        const api = await this.opts.createEventHandlerApi({
            consistentFetchIds: [], // Scheduled actions do not come from the client, so there are no consistent fetch ids.
            delayedActions,
            entry,
            userId,
            scheduledActionId: action.id,
        });

        const isSampled = perf.isSampled();

        const performanceContext: perf.PerformanceContext = {
            type: 'scheduledAction',
            name: action.actionName,
            userId,
        };

        if (isSampled) {
            perf.wrapWithPerformanceTracking(api, performanceContext);
        }

        const eventHandler = async (): Promise<void> => {
            // Execute the action.
            const wrappedAction = isSampled
                ? perf.withPerformanceTracking(scheduledAction.action, performanceContext)
                : scheduledAction.action;

            await wrappedAction(entry.state as Immutable<T['state']>, actionArgs, api);

            this.opts.config.stateSchema.tryValidate(entry.state);

            // Execute delayed actions.
            await resolveDelayedActions(delayedActions, isSampled ? performanceContext : undefined);
        };

        await resolveEventHandlerAndWriteMessage({
            counter: args.counter,
            db: this.opts.db,
            entry,
            event: { type: 'scheduledAction', scheduledActionName: actionName },
            eventHandler,
            now: this.opts.dateNow(),
            userId,
        });
    }

    private getScheduledAction(action: {
        actionName: string;
        isSystemAction?: boolean;
    }): ScheduledActions[string] | undefined {
        return action.isSystemAction
            ? this.systemScheduledActions[action.actionName as keyof SystemScheduledActions]
            : this.scheduledActions[action.actionName];
    }

    private async executeGlobalSystemScheduledAction(action: QueuedScheduledAction): Promise<void> {
        const { actionName, actionArgs } = action;
        const scheduledAction = this.systemScheduledActions[actionName as keyof SystemScheduledActions];

        const validationError = scheduledAction.schema.validate(actionArgs);
        if (validationError) {
            // Ignore scheduled actions with arguments that we do not recognize.
            // They may have been scheduled on a different version of the game.
            logger.warn('Error validating args for action `' + actionName + '`: ' + validationError);
        }

        switch (action.actionName) {
            case SYSTEM_SCHEDULED_ACTION_NAMES.lineChatbotBroadcast: {
                const argsSchema = this.systemScheduledActions.lineChatbotBroadcast.schema;
                const { broadcastId, broadcastOpts } = actionArgs as SB.ExtractType<typeof argsSchema>;

                const createdAt = this.opts.dateNow();

                try {
                    const requestId = await this.opts.narrowcastAPIClient.sendNarrowcastMessage(
                        broadcastId,
                        broadcastOpts,
                    );

                    await this.opts.db.lineChatbotBroadcasts.addBroadcast({
                        broadcastId,
                        broadcastOpts,
                        createdAt,
                        requestId,
                    });
                } catch (error: any) {
                    await this.opts.db.lineChatbotBroadcasts.addBroadcast({
                        broadcastId,
                        broadcastOpts,
                        createdAt,
                        error: error.message,
                    });

                    await this.opts.errorReporter?.captureException(error, { user: { id: action.userId } }, action);

                    // Swallow error to skip scheduled action retries to avoid duplicate broadcasts
                }

                return;
            }
            default: {
                throw Error(`Invalid global system scheduled action name ${action.actionName}`);
            }
        }
    }
}

function isGlobalSystemScheduledAction(action: QueuedScheduledAction): boolean {
    return !!action.isSystemAction && action.userId === GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID;
}

export const scheduledActionToQueuedAction = (
    actionId: string,
    timeslotId: number,
    action: ScheduledAction,
): QueuedScheduledAction => ({
    actionArgs: action.actionArgs,
    actionName: action.actionName,
    // Include timeslot ID in queued action ID to distinguish between two actions
    // with the same name and notification ID scheduled for different timeslots:
    id: actionId + '-' + timeslotId,
    isSystemAction: action.isSystemAction,
    userId: action.userId,
});

/** Queued action IDs include timeslot ID, DB-persisted actions do not. */
export const queuedActionIdToScheduledActionId = (queuedActionId: string): string => queuedActionId.split('-')[0]!;
